import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Cpu, GraduationCap, Code2, Sparkles } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const highlights = [
  {
    id: 'remote',
    icon: Globe,
    title: 'Remote-First, High-Trust',
    description: 'Work from anywhere with full autonomy. We trust our team to deliver excellence on their own terms.',
  },
  {
    id: 'technology',
    icon: Cpu,
    title: 'High-Impact Projects',
    description: 'Build SaaS platforms, AI systems, and growth engines for ambitious companies across industries.',
  },
  {
    id: 'growth',
    icon: GraduationCap,
    title: 'Accelerated Growth',
    description: 'Work directly with senior engineers and founders. Fast learning, real responsibility, clear career progression.',
  },
  {
    id: 'stack',
    icon: Code2,
    title: 'Modern Tech Stack',
    description: 'LangChain, Python, Flutter, React, AWS/GCP — and the freedom to choose the right tools for the job.',
  },
];

const techStack = ['LangChain', 'Python', 'Flutter', 'React', 'AWS', 'GCP', 'TypeScript', 'FastAPI'];

export const CareersSection = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="careers"
      ref={sectionRef}
      data-testid="careers-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Content */}
            <div>
              {/* Section Label */}
              <p
                className="kv-reveal text-xs font-semibold uppercase tracking-[0.2em] mb-3"
                style={{ color: '#0A3D91' }}
                data-testid="careers-label"
              >
                Careers
              </p>

              {/* Headline */}
              <h2
                className="kv-reveal text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
                data-testid="careers-headline"
              >
                Join the Future of{' '}
                <span style={{ color: '#0A3D91' }}>Intelligence.</span>
              </h2>

              {/* Description */}
              <p
                className="kv-reveal text-lg mb-6"
                style={{ color: '#475569', lineHeight: '1.7' }}
              >
                At KritvaSoft, we partner with ambitious companies to build systems that 
                generate millions in revenue. We're looking for exceptional engineers, 
                strategists, and operators who want to work on high-impact projects.
              </p>

              {/* Innovation Badge */}
              <div 
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border mb-6"
                style={{ 
                  borderColor: '#E2E8F0',
                  backgroundColor: 'rgba(10, 61, 145, 0.02)'
                }}
              >
                <Sparkles className="w-4 h-4" style={{ color: '#00BFFF' }} />
                <span className="text-sm" style={{ color: '#475569' }}>
                  We're hiring across engineering, AI, and growth roles
                </span>
              </div>

              {/* CTA Button */}
              <div>
                <Link
                  to="/careers"
                  className="kv-btn-primary px-7 py-3 text-base group"
                  data-testid="careers-cta"
                >
                  Explore Careers
                  <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Right Column - Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((highlight, index) => (
                <div
                  key={highlight.id}
                  className="group p-5 rounded-xl border"
                  style={{ 
                    borderColor: '#E2E8F0',
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'var(--kv-shadow-sm)',
                    transition: 'box-shadow 0.3s cubic-bezier(0.25,0.1,0.25,1), border-color 0.3s cubic-bezier(0.25,0.1,0.25,1), transform 0.3s cubic-bezier(0.25,0.1,0.25,1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--kv-shadow-lg)';
                    e.currentTarget.style.borderColor = '#CBD5E1';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--kv-shadow-sm)';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  data-testid={`careers-highlight-${highlight.id}`}
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors duration-300"
                    style={{ backgroundColor: 'rgba(10, 61, 145, 0.05)' }}
                  >
                    <highlight.icon
                      className="w-5 h-5"
                      style={{ color: '#0A3D91' }}
                    />
                  </div>

                  {/* Title */}
                  <h3
                    className="text-base font-bold mb-1"
                    style={{ color: '#131B2E' }}
                  >
                    {highlight.title}
                  </h3>

                  {/* Description */}
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: '#475569' }}
                  >
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Tech Stack Bar */}
          <div 
            className="mt-6 pt-4 border-t"
            style={{ borderColor: '#E2E8F0' }}
            data-testid="careers-tech-stack"
          >
            <p
              className="text-center text-xs uppercase tracking-wider mb-4"
              style={{ color: '#475569' }}
            >
              Our Technology Stack
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {techStack.map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 hover:border-[#0A3D91] hover:bg-[rgba(10,61,145,0.02)]"
                  style={{ 
                    borderColor: '#E2E8F0',
                    color: '#131B2E'
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Culture Statement */}
          <div 
            className="mt-4 text-center max-w-2xl mx-auto"
            data-testid="careers-culture"
          >
            <p
              className="text-base leading-relaxed italic"
              style={{ color: '#475569' }}
            >
              "We believe in building products that matter, with people who care. 
              If you're passionate about technology and impact, we'd love to hear from you."
            </p>
            <p
              className="mt-3 text-sm font-semibold"
              style={{ color: '#131B2E' }}
            >
              — The KritvaSoft Team
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareersSection;
